import { useStore } from "../contexts/StoreContext";
import { useQuery } from "@tanstack/react-query";
import { fetchDashboardStats } from "../api/queries";
import { useLanguage } from "../contexts/LanguageContext";
import { ErrorBoundary } from "react-error-boundary";
import { safeStorage } from "../utils/storage";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import {
  UserGroupIcon,
  UserPlusIcon,
  GiftIcon,
  SquaresPlusIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowsUpDownIcon,
} from "@heroicons/react/24/outline";

function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div className="p-4 text-center">
      <h2 className="text-lg font-medium text-gray-900">
        Something went wrong
      </h2>
      <p className="mt-2 text-sm text-gray-500">
        Please try refreshing the page
      </p>
      <button
        onClick={resetErrorBoundary}
        className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md"
      >
        Try again
      </button>
    </div>
  );
}

export default function Dashboard() {
  const { selectedStore } = useStore();
  const { t } = useLanguage();
  const today = new Date();

  const { data: stats } = useQuery({
    queryKey: ["dashboardStats", selectedStore?.id],
    queryFn: () => fetchDashboardStats(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  const cards = [
    {
      name: t("dashboard.stats.totalCustomers"),
      value: stats?.totalCustomers || 0,
      change: stats?.totalCustomerGrowth || 0,
      icon: UserPlusIcon,
    },
    {
      name: t("dashboard.stats.activeCustomers"),
      value: stats?.activeCustomers || 0,
      change: stats?.activeCustomerGrowth || 0,
      icon: UserGroupIcon,
    },
    {
      name: t("dashboard.stats.pointsIssued"),
      value: stats?.pointsIssued || 0,
      change: stats?.pointsIssuedGrowth || 0,
      icon: SquaresPlusIcon,
    },
    {
      name: t("dashboard.stats.pointsRedeemed"),
      value: stats?.pointsRedeemed || 0,
      change: stats?.pointsRedeemedGrowth || 0,
      icon: GiftIcon,
    },
  ];

  return (
    <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-6 lg:gap-8 sm:grid-cols-2 md:grid-cols-4">
          {cards.map((card) => (
            <div
              key={card.name}
              className="bg-white overflow-hidden rounded-lg shadow-sm"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <card.icon
                      className="h-6 w-6 text-gray-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {card.name}
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-900">
                          {card.value}
                        </div>
                        <div
                          className={`ml-2 flex items-baseline text-sm font-semibold ${
                            card.change === 0
                              ? "text-amber-600"
                              : card.change > 0
                                ? "text-green-600"
                                : "text-red-600"
                          }`}
                        >
                          {card.change === 0 ? (
                            <ArrowsUpDownIcon
                              className="h-4 w-4 shrink-0"
                              aria-hidden="true"
                            />
                          ) : card.change > 0 ? (
                            <ArrowUpIcon
                              className="h-4 w-4 shrink-0"
                              aria-hidden="true"
                            />
                          ) : (
                            <ArrowDownIcon
                              className="h-4 w-4 shrink-0"
                              aria-hidden="true"
                            />
                          )}
                          <span className="sr-only">
                            {card.change === 0
                              ? t("dashboard.stats.unchanged")
                              : card.change > 0
                                ? t("dashboard.stats.increased")
                                : t("dashboard.stats.decreased")}
                          </span>
                          {Math.abs(card.change)}%
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Customer Growth Chart */}
        <div>
          <h2 className="text-lg font-medium text-gray-900">
            {t("dashboard.customerGrowth.title")}
          </h2>
          <div className="mt-4 bg-white p-6 shadow-sm rounded-lg">
            <div className="h-64 bg-gray-50 rounded-lg">
              {stats?.customerGrowthData && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={stats.customerGrowthData}
                    margin={{
                      top: 20,
                      right: 20,
                      left: 20,
                      bottom: 20,
                    }}
                  >
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date) =>
                        new Date(date).toLocaleDateString(
                          safeStorage.get("preferredLanguage") || "EN",
                          {
                            month: "short",
                            day: "numeric",
                          },
                        )
                      }
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip
                      labelFormatter={(date) =>
                        new Date(date).toLocaleDateString(
                          safeStorage.get("preferredLanguage") || "EN",
                          {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          },
                        )
                      }
                      formatter={(value, name) => [
                        value,
                        t(`dashboard.growthGraph.${name}`),
                      ]}
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "8px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="customers"
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="transactions"
                      stroke="#16a34a"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 gap-6 lg:gap-8 md:grid-cols-2">
          <div>
            <h2 className="text-lg font-medium text-gray-900">
              {t("dashboard.recentPurchaseActivity.title")}
            </h2>
            <div className="mt-4 bg-white shadow-sm rounded-lg">
              {stats?.recentPurchaseActivity?.map((activity, index) => (
                <div
                  key={`purchase-${activity.id}`}
                  className={`px-6 py-4 ${index !== stats.recentPurchaseActivity.length - 1 ? "border-b border-gray-200" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {t(
                          `dashboard.recentPurchaseActivity.${activity.kind}`,
                          { point: activity.point },
                        )}
                      </p>
                      <p className="text-sm text-gray-500">
                        {activity.customer}
                      </p>
                    </div>
                    <p className="text-sm text-gray-500">
                      {(() => {
                        const activityDate = new Date(activity.time);
                        return activityDate.toDateString() ===
                          today.toDateString()
                          ? activityDate.toLocaleTimeString()
                          : activityDate.toLocaleDateString();
                      })()}
                    </p>
                  </div>
                </div>
              ))}
              {(!stats?.recentPurchaseActivity ||
                stats.recentPurchaseActivity.length === 0) && (
                <div className="px-6 py-4 text-sm text-gray-500">
                  {t("dashboard.recentActivity.empty")}
                </div>
              )}
            </div>
          </div>
          <div>
            <h2 className="text-lg font-medium text-gray-900">
              {t("dashboard.recentRedemptionActivity.title")}
            </h2>
            <div className="mt-4 bg-white shadow-sm rounded-lg">
              {stats?.recentRedemptionActivity?.map((activity, index) => (
                <div
                  key={`redemption-${activity.id}`}
                  className={`px-6 py-4 ${index !== stats.recentRedemptionActivity.length - 1 ? "border-b border-gray-200" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {t(
                          `dashboard.recentRedemptionActivity.${activity.kind}`,
                          { point: activity.point },
                        )}
                      </p>
                      <p className="text-sm text-gray-500">
                        {activity.customer}
                      </p>
                    </div>
                    <p className="text-sm text-gray-500">
                      {(() => {
                        const activityDate = new Date(activity.time);
                        return activityDate.toDateString() ===
                          today.toDateString()
                          ? activityDate.toLocaleTimeString()
                          : activityDate.toLocaleDateString();
                      })()}
                    </p>
                  </div>
                </div>
              ))}
              {(!stats?.recentRedemptionActivity ||
                stats.recentRedemptionActivity.length === 0) && (
                <div className="px-6 py-4 text-sm text-gray-500">
                  {t("dashboard.recentActivity.empty")}
                </div>
              )}
            </div>
          </div>
        </div>
      </ErrorBoundary>
    </div>
  );
}
